/**
 * v2.0.1 inbound CALL dispatcher (Session 10b).
 *
 * Translates CSMS-initiated wire frames into version-neutral
 * `IncomingOcppEvent`s. v2.0.1 carries connector addressing as
 * `(evseId, connectorId=local)` tuples; the domain works in global
 * `ConnectorId`s. The dispatcher resolves the tuple via the topology
 * getter passed by the adapter.
 *
 * If topology resolution fails (unknown evseId, no local mapping),
 * the dispatcher emits an `InvalidIncoming` so the handler can reply
 * a Rejected CALLRESULT instead of throwing through the dispatcher.
 *
 * Actions handled:
 *   - RequestStartTransaction → RemoteStartIncoming (evseId → global ConnectorId)
 *   - RequestStopTransaction  → RemoteStopIncoming (string txid → numeric brand)
 *   - Reset                   → ResetIncoming
 *
 * v2.0.1 carries `transactionId` as a string (maxLength 36); the
 * domain `TransactionId` is a numeric brand (int32). Per the
 * decision tracked in Session 10c, the simulator picks the txid
 * itself (CP-owned in v2.0.1) and stringifies on the wire, so we
 * `Number()` back at inbound. Non-numeric txids are rejected as
 * `InvalidIncoming` — the simulator can never receive a UUID-style
 * txid for a transaction it minted as an integer.
 *
 * Anything else falls through to UnknownIncoming (handler emits
 * NotImplemented CALLERROR via the existing dispatch path).
 */
import { ChargingProfileId, EvseId, TransactionId } from '@/domain/types';
import type { ConnectorId } from '@/domain/types';
import type { Topology } from '@/domain/Topology';
import type {
  IncomingOcppEvent,
  InvalidIncoming,
} from '@/protocol/OcppAdapter';

export type TopologyGetter = () => Topology | null;

type IdFactory<T> = { of(raw: number): T };

function tryParseId<T>(
  factory: IdFactory<T>,
  raw: unknown,
): { ok: true; value: T } | { ok: false; reason: string } {
  if (typeof raw !== 'number') {
    return { ok: false, reason: `expected number, got ${typeof raw}` };
  }
  try {
    return { ok: true, value: factory.of(raw) };
  } catch (e) {
    return { ok: false, reason: e instanceof Error ? e.message : String(e) };
  }
}

function invalid(
  messageId: string,
  action: string,
  reason: string,
  rawPayload: Record<string, unknown>,
): InvalidIncoming {
  return { kind: 'Invalid', messageId, action: 'Invalid', originalAction: action, reason, rawPayload };
}

/**
 * Resolve a v2.0.1 (evseId, localConnectorId) wire tuple to a domain global
 * ConnectorId. `evseId === 0` is the station-wide addressing in some v2.0.1
 * actions — caller handles that case before calling this.
 */
function resolveGlobalConnector(
  topology: Topology,
  rawEvseId: number,
  rawLocalConnectorId: number | undefined,
): ConnectorId | null {
  const evseR = tryParseId(EvseId, rawEvseId);
  if (!evseR.ok) return null;
  if (rawLocalConnectorId === undefined) {
    const evse = (() => {
      try {
        return topology.getEvse(evseR.value);
      } catch {
        return null;
      }
    })();
    if (!evse || evse.connectorIds.length === 0) return null;
    return evse.connectorIds[0] as ConnectorId;
  }
  try {
    return topology.getGlobalIdFor(evseR.value, rawLocalConnectorId);
  } catch {
    return null;
  }
}

export function dispatchIncoming(
  messageId: string,
  action: string,
  payload: Record<string, unknown>,
  getTopology: TopologyGetter,
): IncomingOcppEvent {
  switch (action) {
    case 'Reset':
      return {
        kind: 'Reset',
        messageId,
        action,
        type: payload['type'] as 'Hard' | 'Soft',
      };

    case 'RequestStartTransaction': {
      const idTokenObj = payload['idToken'] as Record<string, unknown> | undefined;
      const idTag = idTokenObj?.['idToken'];
      if (typeof idTag !== 'string') {
        return invalid(messageId, action, 'idToken.idToken missing or not a string', payload);
      }
      const rawEvseId = payload['evseId'];
      if (rawEvseId === undefined) {
        return {
          kind: 'RemoteStartTransaction',
          messageId,
          action,
          idTag,
        };
      }
      if (typeof rawEvseId !== 'number') {
        return invalid(messageId, action, `evseId: expected number, got ${typeof rawEvseId}`, payload);
      }
      const topology = getTopology();
      if (!topology) {
        return invalid(messageId, action, 'topology unavailable — cannot resolve evseId', payload);
      }
      const global = resolveGlobalConnector(topology, rawEvseId, undefined);
      if (global === null) {
        return invalid(messageId, action, `evseId ${rawEvseId} unknown to topology`, payload);
      }
      return {
        kind: 'RemoteStartTransaction',
        messageId,
        action,
        idTag,
        connectorId: global,
      };
    }

    case 'GetVariables': {
      const data = payload['getVariableData'];
      if (!Array.isArray(data)) {
        return invalid(messageId, action, 'getVariableData must be an array', payload);
      }
      const entries: { component: string; variable: string; attributeType?: string }[] = [];
      for (const raw of data) {
        if (typeof raw !== 'object' || raw === null) continue;
        const r = raw as Record<string, unknown>;
        const comp = (r['component'] as Record<string, unknown> | undefined)?.['name'];
        const variable = (r['variable'] as Record<string, unknown> | undefined)?.['name'];
        const attr = r['attributeType'];
        if (typeof comp !== 'string' || typeof variable !== 'string') continue;
        entries.push({
          component: comp,
          variable,
          ...(typeof attr === 'string' ? { attributeType: attr } : {}),
        });
      }
      return { kind: 'GetVariables', messageId, action, entries };
    }

    case 'SetVariables': {
      const data = payload['setVariableData'];
      if (!Array.isArray(data)) {
        return invalid(messageId, action, 'setVariableData must be an array', payload);
      }
      const entries: {
        component: string;
        variable: string;
        attributeValue: string;
        attributeType?: string;
      }[] = [];
      for (const raw of data) {
        if (typeof raw !== 'object' || raw === null) continue;
        const r = raw as Record<string, unknown>;
        const comp = (r['component'] as Record<string, unknown> | undefined)?.['name'];
        const variable = (r['variable'] as Record<string, unknown> | undefined)?.['name'];
        const value = r['attributeValue'];
        const attr = r['attributeType'];
        if (typeof comp !== 'string' || typeof variable !== 'string' || typeof value !== 'string') {
          continue;
        }
        entries.push({
          component: comp,
          variable,
          attributeValue: value,
          ...(typeof attr === 'string' ? { attributeType: attr } : {}),
        });
      }
      return { kind: 'SetVariables', messageId, action, entries };
    }

    case 'GetBaseReport': {
      const requestId = payload['requestId'];
      const reportBase = payload['reportBase'];
      if (typeof requestId !== 'number' || !Number.isInteger(requestId)) {
        return invalid(messageId, action, 'requestId must be an integer', payload);
      }
      if (
        reportBase !== 'ConfigurationInventory' &&
        reportBase !== 'FullInventory' &&
        reportBase !== 'SummaryInventory'
      ) {
        return invalid(messageId, action, `reportBase "${String(reportBase)}" unknown`, payload);
      }
      return {
        kind: 'GetBaseReport',
        messageId,
        action,
        requestId,
        reportBase,
      };
    }

    case 'RequestStopTransaction': {
      const raw = payload['transactionId'];
      if (typeof raw !== 'string') {
        return invalid(messageId, action, `transactionId must be a string (v2.0.1)`, payload);
      }
      const asNumber = Number(raw);
      if (!Number.isFinite(asNumber) || !Number.isInteger(asNumber)) {
        return invalid(
          messageId,
          action,
          `transactionId "${raw}" is not a numeric integer — simulator only mints integer txids`,
          payload,
        );
      }
      const r = tryParseId(TransactionId, asNumber);
      if (!r.ok) {
        return invalid(messageId, action, `transactionId: ${r.reason}`, payload);
      }
      return {
        kind: 'RemoteStopTransaction',
        messageId,
        action,
        transactionId: r.value,
      };
    }

    // ───────────────────────────────────────────────────────────────────
    // Session 19a — V201 inbound parity with V16. These map the v2.0.1
    // wire shapes onto the version-neutral IncomingOcppEvent kinds the
    // shared handler (csmsCallHandler.ts) already understands. v2.0.1
    // addresses by `evse: {id, connectorId?}` (or `evseId`+`connectorId`
    // for UnlockConnector) instead of a flat connectorId, so we resolve
    // through the topology getter. NOTE: `evse` with an id but NO
    // connectorId addresses the whole EVSE; we resolve it to the EVSE's
    // FIRST connector (exact for the default 1-connector-per-EVSE
    // topology; a documented simplification for multi-connector EVSEs).
    // ───────────────────────────────────────────────────────────────────
    case 'ChangeAvailability': {
      const operationalStatus = payload['operationalStatus'];
      if (operationalStatus !== 'Operative' && operationalStatus !== 'Inoperative') {
        return invalid(
          messageId,
          action,
          `operationalStatus "${String(operationalStatus)}" invalid`,
          payload,
        );
      }
      const evse = payload['evse'] as Record<string, unknown> | undefined;
      if (evse === undefined) {
        // No evse → whole charging station (connectorId 0).
        return { kind: 'ChangeAvailability', messageId, action, connectorId: 0, type: operationalStatus };
      }
      const resolved = resolveEvseToConnector(evse, getTopology());
      if (resolved.kind === 'error') {
        return invalid(messageId, action, resolved.reason, payload);
      }
      return {
        kind: 'ChangeAvailability',
        messageId,
        action,
        connectorId: resolved.connectorId,
        type: operationalStatus,
      };
    }

    case 'TriggerMessage': {
      const requestedMessage = payload['requestedMessage'];
      if (typeof requestedMessage !== 'string') {
        return invalid(messageId, action, 'requestedMessage missing', payload);
      }
      const evse = payload['evse'] as Record<string, unknown> | undefined;
      if (evse === undefined) {
        return { kind: 'TriggerMessage', messageId, action, requestedMessage };
      }
      const resolved = resolveEvseToConnector(evse, getTopology());
      if (resolved.kind === 'error') {
        return invalid(messageId, action, resolved.reason, payload);
      }
      return {
        kind: 'TriggerMessage',
        messageId,
        action,
        requestedMessage,
        connectorId: resolved.connectorId,
      };
    }

    case 'UnlockConnector': {
      // v2.0.1 carries evseId + connectorId as separate required integers
      // (not an `evse` object).
      const rawEvseId = payload['evseId'];
      const rawConnId = payload['connectorId'];
      if (typeof rawEvseId !== 'number' || typeof rawConnId !== 'number') {
        return invalid(messageId, action, 'evseId and connectorId must be numbers', payload);
      }
      const topology = getTopology();
      if (!topology) {
        return invalid(messageId, action, 'topology unavailable — cannot resolve evse', payload);
      }
      const global = resolveGlobalConnector(topology, rawEvseId, rawConnId);
      if (global === null) {
        return invalid(
          messageId,
          action,
          `evse {id:${rawEvseId}, connector:${rawConnId}} unknown to topology`,
          payload,
        );
      }
      return { kind: 'UnlockConnector', messageId, action, connectorId: global };
    }

    case 'ClearCache':
      // Empty payload (same as v1.6). Handler wipes the per-station AuthCache.
      return { kind: 'ClearCache', messageId, action };

    case 'GetLocalListVersion':
      // Empty payload. Handler replies version-aware (versionNumber for v201).
      return { kind: 'GetLocalListVersion', messageId, action };

    case 'SendLocalList': {
      // v2.0.1 uses `versionNumber` (v1.6: `listVersion`) and nests the tag
      // under `idToken.idToken`. Normalise each AuthorizationData entry to
      // the v16-shaped {idTag, idTagInfo} the LocalAuthList domain expects,
      // then carry `listVersion` so the shared handler is version-neutral.
      const versionNumber = payload['versionNumber'];
      if (typeof versionNumber !== 'number' || !Number.isInteger(versionNumber) || versionNumber < 0) {
        return invalid(messageId, action, 'versionNumber must be a non-negative integer', payload);
      }
      const updateType = payload['updateType'];
      if (updateType !== 'Full' && updateType !== 'Differential') {
        return invalid(
          messageId,
          action,
          `updateType must be Full or Differential, got ${String(updateType)}`,
          payload,
        );
      }
      const rawList = Array.isArray(payload['localAuthorizationList'])
        ? (payload['localAuthorizationList'] as Record<string, unknown>[])
        : [];
      return {
        kind: 'SendLocalList',
        messageId,
        action,
        listVersion: versionNumber,
        updateType,
        localAuthorizationList: rawList.map(normaliseV201AuthData),
      };
    }

    case 'GetTransactionStatus': {
      const raw = payload['transactionId'];
      if (raw === undefined) {
        // No id → "is ANY transaction ongoing?" query.
        return { kind: 'GetTransactionStatus', messageId, action };
      }
      if (typeof raw !== 'string') {
        return invalid(messageId, action, 'transactionId must be a string (v2.0.1)', payload);
      }
      const asNumber = Number(raw);
      if (!Number.isFinite(asNumber) || !Number.isInteger(asNumber)) {
        // The simulator only mints positive integer txids; a non-numeric id
        // is for a transaction we never issued. Use -1 (impossible to match)
        // so the handler reports ongoingIndicator=false rather than falling
        // back to the "any ongoing" query.
        return { kind: 'GetTransactionStatus', messageId, action, transactionId: -1 };
      }
      return { kind: 'GetTransactionStatus', messageId, action, transactionId: asNumber };
    }

    case 'DataTransfer':
      return {
        kind: 'DataTransfer',
        messageId,
        action,
        vendorId: payload['vendorId'] as string,
        ...(typeof payload['messageId'] === 'string'
          ? { payloadMessageId: payload['messageId'] }
          : {}),
        ...(typeof payload['data'] === 'string' ? { data: payload['data'] } : {}),
      };

    // ───────────────────────────────────────────────────────────────────
    // Session 19c — Smart Charging. v2.0.1 addresses by `evseId` (0 =
    // station-wide); the version-neutral events use global ConnectorId|0.
    // SetChargingProfile / ClearChargingProfile reuse the existing shared
    // handler unchanged — the dispatcher just reshapes the v2.0.1 wire.
    // ───────────────────────────────────────────────────────────────────
    case 'SetChargingProfile': {
      const rawEvseId = payload['evseId'];
      if (typeof rawEvseId !== 'number') {
        return invalid(messageId, action, 'evseId must be a number', payload);
      }
      const conn = evseIdToConnector(rawEvseId, getTopology());
      if (conn.kind === 'error') return invalid(messageId, action, conn.reason, payload);
      const cp = payload['chargingProfile'];
      if (typeof cp !== 'object' || cp === null) {
        return invalid(messageId, action, 'chargingProfile missing', payload);
      }
      return {
        kind: 'SetChargingProfile',
        messageId,
        action,
        connectorId: conn.connectorId,
        csChargingProfile: reshapeV201ChargingProfile(cp as Record<string, unknown>),
      };
    }

    case 'ClearChargingProfile': {
      // v2.0.1 wraps the filter in `chargingProfileCriteria` + a top-level
      // `chargingProfileId`. Map onto the shared ClearChargingProfile event.
      const out: {
        kind: 'ClearChargingProfile';
        messageId: string;
        action: string;
        id?: ChargingProfileId;
        connectorId?: ConnectorId;
        chargingProfilePurpose?: string;
        stackLevel?: number;
      } = { kind: 'ClearChargingProfile', messageId, action };
      if (payload['chargingProfileId'] !== undefined) {
        const r = tryParseId(ChargingProfileId, payload['chargingProfileId']);
        if (!r.ok) return invalid(messageId, action, `chargingProfileId: ${r.reason}`, payload);
        out.id = r.value;
      }
      const crit = payload['chargingProfileCriteria'] as Record<string, unknown> | undefined;
      if (crit) {
        if (typeof crit['chargingProfilePurpose'] === 'string') {
          out.chargingProfilePurpose = wirePurposeToDomain(crit['chargingProfilePurpose']) as string;
        }
        if (typeof crit['stackLevel'] === 'number') out.stackLevel = crit['stackLevel'];
        if (typeof crit['evseId'] === 'number' && crit['evseId'] !== 0) {
          const conn = evseIdToConnector(crit['evseId'], getTopology());
          if (conn.kind === 'error') return invalid(messageId, action, conn.reason, payload);
          if (conn.connectorId !== 0) out.connectorId = conn.connectorId;
        }
      }
      return out;
    }

    case 'GetCompositeSchedule': {
      const rawEvseId = payload['evseId'];
      const duration = payload['duration'];
      if (typeof rawEvseId !== 'number' || typeof duration !== 'number') {
        return invalid(messageId, action, 'evseId and duration must be numbers', payload);
      }
      const topology = getTopology();
      if (!topology) {
        return invalid(messageId, action, 'topology unavailable — cannot resolve evse', payload);
      }
      // GetCompositeSchedule needs a concrete connector (no station-wide
      // composite in the simulator). evseId 0 is not meaningful here.
      const global = resolveGlobalConnector(topology, rawEvseId, undefined);
      if (global === null) {
        return invalid(messageId, action, `evseId ${rawEvseId} unknown to topology`, payload);
      }
      return {
        kind: 'GetCompositeSchedule',
        messageId,
        action,
        connectorId: global,
        duration,
        ...(payload['chargingRateUnit'] === 'A' || payload['chargingRateUnit'] === 'W'
          ? { chargingRateUnit: payload['chargingRateUnit'] }
          : {}),
      };
    }

    case 'GetChargingProfiles': {
      const requestId = payload['requestId'];
      if (typeof requestId !== 'number' || !Number.isInteger(requestId)) {
        return invalid(messageId, action, 'requestId must be an integer', payload);
      }
      let connectorId: ConnectorId | 0 = 0;
      if (typeof payload['evseId'] === 'number' && payload['evseId'] !== 0) {
        const conn = evseIdToConnector(payload['evseId'], getTopology());
        if (conn.kind === 'error') return invalid(messageId, action, conn.reason, payload);
        connectorId = conn.connectorId;
      }
      const crit = payload['chargingProfile'] as Record<string, unknown> | undefined;
      const purpose = wirePurposeToDomain(crit?.['chargingProfilePurpose']);
      const stackLevel = crit?.['stackLevel'];
      const ids = crit?.['chargingProfileId'];
      return {
        kind: 'GetChargingProfiles',
        messageId,
        action,
        requestId,
        connectorId,
        ...(typeof purpose === 'string' ? { chargingProfilePurpose: purpose } : {}),
        ...(typeof stackLevel === 'number' ? { stackLevel } : {}),
        ...(Array.isArray(ids) ? { chargingProfileId: ids.filter((x): x is number => typeof x === 'number') } : {}),
      };
    }

    // ───────────────────────────────────────────────────────────────────
    // Session 19d — Display Messages (v2.0.1-only). No topology resolution
    // needed; the handler does domain parsing + persistence.
    // ───────────────────────────────────────────────────────────────────
    case 'SetDisplayMessage': {
      const msg = payload['message'];
      if (typeof msg !== 'object' || msg === null) {
        return invalid(messageId, action, 'message missing', payload);
      }
      return {
        kind: 'SetDisplayMessage',
        messageId,
        action,
        message: msg as Record<string, unknown>,
      };
    }

    case 'ClearDisplayMessage': {
      const id = payload['id'];
      if (typeof id !== 'number' || !Number.isInteger(id)) {
        return invalid(messageId, action, 'id must be an integer', payload);
      }
      return { kind: 'ClearDisplayMessage', messageId, action, id };
    }

    case 'GetDisplayMessages': {
      const requestId = payload['requestId'];
      if (typeof requestId !== 'number' || !Number.isInteger(requestId)) {
        return invalid(messageId, action, 'requestId must be an integer', payload);
      }
      const rawIds = payload['id'];
      return {
        kind: 'GetDisplayMessages',
        messageId,
        action,
        requestId,
        ...(Array.isArray(rawIds)
          ? { ids: rawIds.filter((x): x is number => typeof x === 'number') }
          : {}),
        ...(typeof payload['priority'] === 'string' ? { priority: payload['priority'] } : {}),
        ...(typeof payload['state'] === 'string' ? { state: payload['state'] } : {}),
      };
    }

    case 'ReserveNow': {
      // Session 21 — v2.0.1 Reservation. `id` → reservationId,
      // idToken.idToken → idTag, optional evseId → connector (0 = any).
      const reservationId = payload['id'];
      const expiryDate = payload['expiryDateTime'];
      const idTokenObj = payload['idToken'] as Record<string, unknown> | undefined;
      const idTag = idTokenObj?.['idToken'];
      if (typeof reservationId !== 'number' || !Number.isInteger(reservationId)) {
        return invalid(messageId, action, 'id must be an integer', payload);
      }
      if (typeof expiryDate !== 'string' || typeof idTag !== 'string') {
        return invalid(messageId, action, 'expiryDateTime + idToken.idToken required', payload);
      }
      let connectorId: ConnectorId | 0 = 0;
      if (typeof payload['evseId'] === 'number' && payload['evseId'] !== 0) {
        const conn = evseIdToConnector(payload['evseId'], getTopology());
        if (conn.kind === 'error') return invalid(messageId, action, conn.reason, payload);
        connectorId = conn.connectorId;
      }
      return { kind: 'ReserveNow', messageId, action, reservationId, connectorId, idTag, expiryDate };
    }
    case 'CancelReservation': {
      const reservationId = payload['reservationId'];
      if (typeof reservationId !== 'number' || !Number.isInteger(reservationId)) {
        return invalid(messageId, action, 'reservationId must be an integer', payload);
      }
      return { kind: 'CancelReservation', messageId, action, reservationId };
    }

    case 'UpdateFirmware': {
      // Session 20 — v2.0.1 firmware management. requestId + nested
      // `firmware: { location, retrieveDateTime?, ... }`.
      const requestId = payload['requestId'];
      if (typeof requestId !== 'number' || !Number.isInteger(requestId)) {
        return invalid(messageId, action, 'requestId must be an integer', payload);
      }
      const fw = payload['firmware'] as Record<string, unknown> | undefined;
      const location = fw?.['location'];
      if (typeof location !== 'string') {
        return invalid(messageId, action, 'firmware.location must be a string', payload);
      }
      return {
        kind: 'UpdateFirmware',
        messageId,
        action,
        location,
        requestId,
        ...(typeof fw?.['retrieveDateTime'] === 'string'
          ? { retrieveDate: fw['retrieveDateTime'] }
          : {}),
      };
    }

    case 'PublishFirmware': {
      // Local Controller role — CSMS publishes a firmware image to the LC.
      // Flat payload: location + checksum + requestId required (already
      // Ajv-validated upstream); retries / retryInterval optional.
      const location = payload['location'];
      const checksum = payload['checksum'];
      const requestId = payload['requestId'];
      if (typeof location !== 'string') {
        return invalid(messageId, action, 'location must be a string', payload);
      }
      if (typeof checksum !== 'string') {
        return invalid(messageId, action, 'checksum must be a string', payload);
      }
      if (typeof requestId !== 'number' || !Number.isInteger(requestId)) {
        return invalid(messageId, action, 'requestId must be an integer', payload);
      }
      return {
        kind: 'PublishFirmware',
        messageId,
        action,
        location,
        checksum,
        requestId,
        ...(typeof payload['retries'] === 'number' ? { retries: payload['retries'] } : {}),
        ...(typeof payload['retryInterval'] === 'number'
          ? { retryInterval: payload['retryInterval'] }
          : {}),
      };
    }

    case 'UnpublishFirmware': {
      // Local Controller role — stop publishing the firmware with this MD5
      // checksum (already Ajv-validated upstream: checksum required).
      const checksum = payload['checksum'];
      if (typeof checksum !== 'string') {
        return invalid(messageId, action, 'checksum must be a string', payload);
      }
      return { kind: 'UnpublishFirmware', messageId, action, checksum };
    }

    case 'GetLog': {
      // Session 19e — diagnostics/security log upload request. Pull the
      // upload target out of the `log` wrapper.
      const requestId = payload['requestId'];
      const logType = payload['logType'];
      if (typeof requestId !== 'number' || !Number.isInteger(requestId)) {
        return invalid(messageId, action, 'requestId must be an integer', payload);
      }
      if (typeof logType !== 'string') {
        return invalid(messageId, action, 'logType missing', payload);
      }
      const log = payload['log'] as Record<string, unknown> | undefined;
      const remoteLocation = log?.['remoteLocation'];
      if (typeof remoteLocation !== 'string') {
        return invalid(messageId, action, 'log.remoteLocation missing', payload);
      }
      return {
        kind: 'GetLog',
        messageId,
        action,
        requestId,
        logType,
        remoteLocation,
        ...(typeof log?.['oldestTimestamp'] === 'string'
          ? { oldestTimestamp: log['oldestTimestamp'] }
          : {}),
        ...(typeof log?.['latestTimestamp'] === 'string'
          ? { latestTimestamp: log['latestTimestamp'] }
          : {}),
      };
    }

    // ───────────────────────────────────────────────────────────────────
    // Session 25 — certificate management (v2.0.1-only).
    // ───────────────────────────────────────────────────────────────────
    case 'CertificateSigned': {
      if (typeof payload['certificateChain'] !== 'string') {
        return invalid(messageId, action, 'certificateChain must be a string', payload);
      }
      return {
        kind: 'CertificateSigned',
        messageId,
        action,
        ...(typeof payload['certificateType'] === 'string'
          ? { certificateType: payload['certificateType'] }
          : {}),
      };
    }
    case 'InstallCertificate': {
      const certificateType = payload['certificateType'];
      const certificate = payload['certificate'];
      if (typeof certificateType !== 'string' || typeof certificate !== 'string') {
        return invalid(messageId, action, 'certificateType + certificate required', payload);
      }
      return { kind: 'InstallCertificate', messageId, action, certificateType, certificate };
    }
    case 'DeleteCertificate': {
      const hashData = payload['certificateHashData'];
      if (typeof hashData !== 'object' || hashData === null) {
        return invalid(messageId, action, 'certificateHashData missing', payload);
      }
      return {
        kind: 'DeleteCertificate',
        messageId,
        action,
        hashData: hashData as Record<string, unknown>,
      };
    }
    case 'GetInstalledCertificateIds': {
      const raw = payload['certificateType'];
      return {
        kind: 'GetInstalledCertificateIds',
        messageId,
        action,
        certificateTypes: Array.isArray(raw)
          ? raw.filter((x): x is string => typeof x === 'string')
          : [],
      };
    }

    case 'SetNetworkProfile': {
      // Session 24 — store an alternate CSMS connection profile in a slot.
      const configurationSlot = payload['configurationSlot'];
      const connectionData = payload['connectionData'];
      if (typeof configurationSlot !== 'number' || !Number.isInteger(configurationSlot)) {
        return invalid(messageId, action, 'configurationSlot must be an integer', payload);
      }
      if (typeof connectionData !== 'object' || connectionData === null) {
        return invalid(messageId, action, 'connectionData missing', payload);
      }
      return {
        kind: 'SetNetworkProfile',
        messageId,
        action,
        configurationSlot,
        connectionData: connectionData as Record<string, unknown>,
      };
    }
    case 'CustomerInformation': {
      // Session 24 — report / clear customer data.
      const requestId = payload['requestId'];
      if (typeof requestId !== 'number' || !Number.isInteger(requestId)) {
        return invalid(messageId, action, 'requestId must be an integer', payload);
      }
      const idTokenObj = payload['idToken'] as Record<string, unknown> | undefined;
      return {
        kind: 'CustomerInformation',
        messageId,
        action,
        requestId,
        report: payload['report'] === true,
        clear: payload['clear'] === true,
        ...(typeof payload['customerIdentifier'] === 'string'
          ? { customerIdentifier: payload['customerIdentifier'] }
          : {}),
        hasIdToken: typeof idTokenObj?.['idToken'] === 'string',
        hasCertificate: typeof payload['customerCertificate'] === 'object',
      };
    }
    case 'CostUpdated': {
      // Session 23 — running transaction cost. transactionId is a string;
      // Number() it (the simulator mints integer ids).
      const totalCost = payload['totalCost'];
      const raw = payload['transactionId'];
      if (typeof totalCost !== 'number' || typeof raw !== 'string') {
        return invalid(messageId, action, 'totalCost (number) + transactionId (string) required', payload);
      }
      return { kind: 'CostUpdated', messageId, action, totalCost, transactionId: Number(raw) };
    }

    // ───────────────────────────────────────────────────────────────────
    // Session 22 — variable Monitoring (v2.0.1-only).
    // ───────────────────────────────────────────────────────────────────
    case 'SetVariableMonitoring': {
      const raw = payload['setMonitoringData'];
      if (!Array.isArray(raw)) {
        return invalid(messageId, action, 'setMonitoringData must be an array', payload);
      }
      const data: {
        id?: number;
        component: string;
        variable: string;
        type: string;
        value: number;
        severity: number;
        transaction: boolean;
      }[] = [];
      for (const item of raw) {
        if (typeof item !== 'object' || item === null) continue;
        const r = item as Record<string, unknown>;
        const component = (r['component'] as Record<string, unknown> | undefined)?.['name'];
        const variable = (r['variable'] as Record<string, unknown> | undefined)?.['name'];
        if (
          typeof component !== 'string' ||
          typeof variable !== 'string' ||
          typeof r['type'] !== 'string' ||
          typeof r['value'] !== 'number' ||
          typeof r['severity'] !== 'number'
        ) {
          continue;
        }
        data.push({
          ...(typeof r['id'] === 'number' ? { id: r['id'] } : {}),
          component,
          variable,
          type: r['type'],
          value: r['value'],
          severity: r['severity'],
          transaction: r['transaction'] === true,
        });
      }
      return { kind: 'SetVariableMonitoring', messageId, action, data };
    }
    case 'ClearVariableMonitoring': {
      const raw = payload['id'];
      if (!Array.isArray(raw)) {
        return invalid(messageId, action, 'id must be an array', payload);
      }
      return {
        kind: 'ClearVariableMonitoring',
        messageId,
        action,
        ids: raw.filter((x): x is number => typeof x === 'number'),
      };
    }
    case 'GetMonitoringReport': {
      const requestId = payload['requestId'];
      if (typeof requestId !== 'number' || !Number.isInteger(requestId)) {
        return invalid(messageId, action, 'requestId must be an integer', payload);
      }
      const rawFilter = payload['componentVariable'];
      const filter: { component: string; variable?: string }[] = [];
      if (Array.isArray(rawFilter)) {
        for (const f of rawFilter) {
          if (typeof f !== 'object' || f === null) continue;
          const fr = f as Record<string, unknown>;
          const comp = (fr['component'] as Record<string, unknown> | undefined)?.['name'];
          if (typeof comp !== 'string') continue;
          const variable = (fr['variable'] as Record<string, unknown> | undefined)?.['name'];
          filter.push({ component: comp, ...(typeof variable === 'string' ? { variable } : {}) });
        }
      }
      return { kind: 'GetMonitoringReport', messageId, action, requestId, filter };
    }
    case 'SetMonitoringBase': {
      const monitoringBase = payload['monitoringBase'];
      if (typeof monitoringBase !== 'string') {
        return invalid(messageId, action, 'monitoringBase missing', payload);
      }
      return { kind: 'SetMonitoringBase', messageId, action, monitoringBase };
    }
    case 'SetMonitoringLevel': {
      const severity = payload['severity'];
      if (typeof severity !== 'number' || !Number.isInteger(severity)) {
        return invalid(messageId, action, 'severity must be an integer', payload);
      }
      return { kind: 'SetMonitoringLevel', messageId, action, severity };
    }

    case 'GetReport': {
      // Session 19b — Device Model inventory request. Parse the optional
      // componentVariable[] filter into flat {component, variable?} pairs.
      const requestId = payload['requestId'];
      if (typeof requestId !== 'number' || !Number.isInteger(requestId)) {
        return invalid(messageId, action, 'requestId must be an integer', payload);
      }
      const rawFilter = payload['componentVariable'];
      const filter: { component: string; variable?: string }[] = [];
      if (Array.isArray(rawFilter)) {
        for (const raw of rawFilter) {
          if (typeof raw !== 'object' || raw === null) continue;
          const r = raw as Record<string, unknown>;
          const comp = (r['component'] as Record<string, unknown> | undefined)?.['name'];
          if (typeof comp !== 'string') continue;
          const variable = (r['variable'] as Record<string, unknown> | undefined)?.['name'];
          filter.push({
            component: comp,
            ...(typeof variable === 'string' ? { variable } : {}),
          });
        }
      }
      return { kind: 'GetReport', messageId, action, requestId, filter };
    }

    default:
      return { kind: 'Unknown', messageId, action, rawPayload: payload };
  }
}

/**
 * Resolve a v2.0.1 `evse: {id, connectorId?}` object to a global
 * ConnectorId. Returns a discriminated result so callers can surface a
 * precise Invalid reason. `connectorId` omitted → the EVSE's first
 * connector (see dispatcher note on the multi-connector simplification).
 */
function resolveEvseToConnector(
  evse: Record<string, unknown>,
  topology: Topology | null,
): { kind: 'ok'; connectorId: ConnectorId } | { kind: 'error'; reason: string } {
  const rawEvseId = evse['id'];
  if (typeof rawEvseId !== 'number') {
    return { kind: 'error', reason: 'evse.id must be a number' };
  }
  if (!topology) {
    return { kind: 'error', reason: 'topology unavailable — cannot resolve evse' };
  }
  const rawConnId = evse['connectorId'];
  const global = resolveGlobalConnector(
    topology,
    rawEvseId,
    typeof rawConnId === 'number' ? rawConnId : undefined,
  );
  if (global === null) {
    return { kind: 'error', reason: `evse {id:${rawEvseId}} unknown to topology` };
  }
  return { kind: 'ok', connectorId: global };
}

/**
 * Session 19c — resolve a v2.0.1 `evseId` to a global ConnectorId for
 * smart-charging actions. `evseId 0` → station-wide (connectorId 0);
 * a positive id → the EVSE's first connector (the multi-connector
 * simplification documented on the dispatcher).
 */
function evseIdToConnector(
  rawEvseId: number,
  topology: Topology | null,
): { kind: 'ok'; connectorId: ConnectorId | 0 } | { kind: 'error'; reason: string } {
  if (rawEvseId === 0) return { kind: 'ok', connectorId: 0 };
  if (!topology) return { kind: 'error', reason: 'topology unavailable — cannot resolve evseId' };
  const global = resolveGlobalConnector(topology, rawEvseId, undefined);
  if (global === null) return { kind: 'error', reason: `evseId ${rawEvseId} unknown to topology` };
  return { kind: 'ok', connectorId: global };
}

/**
 * Session 19c — reshape a v2.0.1 `chargingProfile` object into the
 * v16-shaped `csChargingProfile` the shared `parseSetChargingProfilePayload`
 * consumes. Differences handled: `id` → `chargingProfileId`; the v2.0.1
 * `chargingSchedule` ARRAY collapses to its first element (the simulator
 * tracks a single schedule per profile); a string `transactionId` is
 * converted to the numeric form the domain mints. Period shapes
 * (startPeriod/limit/chargingRateUnit) are wire-compatible.
 */
/**
 * v2.0.1 names the station-wide ceiling "ChargingStationMaxProfile"; the
 * version-neutral domain (and the shared v16 parser) call it
 * "ChargePointMaxProfile". Translate at every inbound purpose entry point
 * (Set/Clear/Get) so v2.0.1 max profiles install, clear, and query correctly.
 */
function wirePurposeToDomain(raw: unknown): unknown {
  return raw === 'ChargingStationMaxProfile' ? 'ChargePointMaxProfile' : raw;
}

function reshapeV201ChargingProfile(cp: Record<string, unknown>): Record<string, unknown> {
  const schedules = Array.isArray(cp['chargingSchedule']) ? cp['chargingSchedule'] : [];
  const firstSchedule =
    schedules.length > 0 && typeof schedules[0] === 'object' && schedules[0] !== null
      ? (schedules[0] as Record<string, unknown>)
      : {};
  const out: Record<string, unknown> = {
    chargingProfileId: cp['id'],
    stackLevel: cp['stackLevel'],
    chargingProfilePurpose: wirePurposeToDomain(cp['chargingProfilePurpose']),
    chargingProfileKind: cp['chargingProfileKind'],
    chargingSchedule: firstSchedule,
    ...(cp['recurrencyKind'] !== undefined ? { recurrencyKind: cp['recurrencyKind'] } : {}),
    ...(cp['validFrom'] !== undefined ? { validFrom: cp['validFrom'] } : {}),
    ...(cp['validTo'] !== undefined ? { validTo: cp['validTo'] } : {}),
  };
  if (typeof cp['transactionId'] === 'string') {
    const n = Number(cp['transactionId']);
    if (Number.isInteger(n)) out['transactionId'] = n;
  }
  return out;
}

/**
 * Map a v2.0.1 AuthorizationData entry to the v16-shaped
 * `{idTag, idTagInfo}` the LocalAuthList domain consumes. v2.0.1 nests
 * the tag under `idToken.idToken` (the type discriminator is dropped —
 * the simulator keys its list by the raw token string) and the decision
 * under `idTokenInfo`. A missing `idTokenInfo` is preserved as a missing
 * `idTagInfo` so the domain's differential-delete semantics still fire.
 *
 * Caveat: the domain AuthStatus enum is v1.6-flavoured (Accepted | Blocked
 * | Expired | Invalid | ConcurrentTx). v2.0.1-exclusive statuses
 * (NoCredit, NotAllowedTypeEVSE, …) fail domain validation → the handler
 * replies SendLocalList=Failed. Extending the status enum is out of 19a's
 * scope.
 */
function normaliseV201AuthData(raw: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const idTokenObj = raw['idToken'] as Record<string, unknown> | undefined;
  if (idTokenObj && typeof idTokenObj['idToken'] === 'string') {
    out['idTag'] = idTokenObj['idToken'];
  }
  const info = raw['idTokenInfo'] as Record<string, unknown> | undefined;
  if (info && typeof info === 'object') {
    const idTagInfo: Record<string, unknown> = {};
    if (typeof info['status'] === 'string') idTagInfo['status'] = info['status'];
    if (typeof info['cacheExpiryDateTime'] === 'string') {
      idTagInfo['expiryDate'] = info['cacheExpiryDateTime'];
    }
    const group = info['groupIdToken'] as Record<string, unknown> | undefined;
    if (group && typeof group['idToken'] === 'string') {
      idTagInfo['parentIdTag'] = group['idToken'];
    }
    out['idTagInfo'] = idTagInfo;
  }
  return out;
}
